import { FormControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import type { RegistrationStage as ProtoRegistrationStage } from '@vendasta/protos/sms/v2/enums';
import { RegistrationStage, VENDASTA_OWNED_CAMPAIGN_FIELDS } from './a2p-registration.constants';

export function isVendastaOwnedField(field: string): boolean {
  const normalized = field.toLowerCase();
  return (
    VENDASTA_OWNED_CAMPAIGN_FIELDS.has(normalized) ||
    normalized.startsWith('sample_message') ||
    normalized.startsWith('message_sample')
  );
}

/**
 * Translates the registration stage the SMS service resolved into the enum this library
 * uses.
 *
 * The stage is deliberately not derived here any more. It used to be computed from the
 * registration form plus the status, which meant a business that holds Twilio's decisions
 * without ever having filled the form in — one that registered before it moved onto this
 * flow — read as "not submitted" and was invited to register again. The service owns that
 * judgement and the send gate already obeys it, so asking it is the only way the two can
 * agree.
 */
export function stageFromProtoRegistrationStage(stage: ProtoRegistrationStage | undefined): RegistrationStage {
  switch (stage) {
    case 'REGISTRATION_STAGE_COMPLETE':
      return RegistrationStage.RegistrationComplete;
    case 'REGISTRATION_STAGE_REVIEWING':
      return RegistrationStage.ReviewingInfo;
    case 'REGISTRATION_STAGE_SUBMISSION_ERROR':
      return RegistrationStage.DataError;
    case 'REGISTRATION_STAGE_CAMPAIGN_ERROR':
      return RegistrationStage.CampaignError;
    case 'REGISTRATION_STAGE_NOT_SUBMITTED':
    default:
      return RegistrationStage.NotSubmitted;
  }
}

export function createGroupValueChangeValidator(
  groupOfFields: string[],
  initialValues: Map<string, string>,
  getAllControls: () => Record<string, FormControl>,
): ValidatorFn {
  const originalValuesMap = new Map<string, string>();
  groupOfFields.forEach((fieldName) => {
    const val = initialValues.get(fieldName);
    if (val !== undefined) {
      originalValuesMap.set(fieldName, val);
    }
  });

  return (): ValidationErrors | null => {
    const controls = getAllControls();
    for (const [fieldName, originalValue] of originalValuesMap.entries()) {
      const c = controls[fieldName];
      if (c !== undefined && c.value !== originalValue) {
        return null;
      }
    }
    return { groupValueError: true };
  };
}

export function isBroadlySubAccount(subaccountId: string | undefined): boolean {
  return !!subaccountId && subaccountId.startsWith('BROADLY:');
}
